import { Text } from '@user-portal/wikr-components';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getCurrentPlanRequest } from 'store/personalPlan/actions';
import { selectCancellationCandidates } from 'store/subscriptions/selectors';

import { sendAnalyticCancelOfferClick, trackScreenLoadCancelOffer } from 'services/Analytics';

import { useDispatch, useSelector } from 'hooks';
import useModals from 'modals/hooks';
import { ModalName } from 'modals/types';

import isMobile from 'helpers/isMobile';
import { getValuesByKey } from 'helpers/utils';

import { CustomPrimaryButton } from 'components/Buttons';
import OutlinedButton from 'components/Buttons/OutlinedButton';
import InstallAppSteps from 'components/InstallAppSteps';
import Modal from 'components/Modal';

import { CancelFlowOffers, CancelOfferAction } from 'types/subscription';

import useCancelSubscriptionFlow from '../../../hooks/useCancelSubscriptionFlow';
import AutoSlider from '../../components/AutoSlider';
import FullScreenModalHeader from '../../components/FullScreenModalHeader';

import PersonalPlanCard from './components/PersonalPlanCard';
import { useGetSliderConfig } from './config';
import * as S from './styles';

const deepLinkPP = 'https://yogago.onelink.me/vaBt/zos8l5ya';

const AdditionalContentOfferModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { closeModal, openModal } = useModals();
    const cancellationCandidates = useSelector(selectCancellationCandidates);
    const [isExploreYogaTypesDesktop, setExploreYogaTypesDesktop] = useState(false);
    const { onCancelSubscription } = useCancelSubscriptionFlow();

    const offer = CancelFlowOffers.ChangePlan;

    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    useEffect(() => {
        dispatch(getCurrentPlanRequest());
    }, [dispatch]);

    useEffect(() => {
        trackScreenLoadCancelOffer({ eventLabel: { subscription_id, offer } });
    }, [subscription_id, offer]);

    const isDesktop = !isMobile.any();

    const onBackButtonClick = () => {
        sendAnalyticCancelOfferClick({ eventLabel: { subscription_id, offer }, actionEvent: CancelOfferAction.Close });

        if (isExploreYogaTypesDesktop) {
            setExploreYogaTypesDesktop(false);
        } else {
            openModal(ModalName.CancellationReasonModal);
        }
    };

    const onClose = useCallback(() => {
        sendAnalyticCancelOfferClick({ eventLabel: { subscription_id, offer }, actionEvent: CancelOfferAction.Close });

        closeModal();
    }, [closeModal, subscription_id, offer]);

    const handleDownloadApp = () => {
        if (isDesktop) {
            setExploreYogaTypesDesktop(true);
        } else {
            window.open(deepLinkPP, '_blank', 'noopener,noreferrer');
        }

        sendAnalyticCancelOfferClick({
            eventLabel: { subscription_id, offer },
            actionEvent: CancelOfferAction.Accept,
        });
    };

    const sliderConfig = useGetSliderConfig();

    return (
        <Modal
            fullscreen
            isOpen
            customStyles={{ padding: 0 }}
            withRoundedCorners={false}
            dataTestId="planSuggestionsModalA"
            data-testid="planSuggestionsModalA"
        >
            <FullScreenModalHeader closeModal={onClose} onBackButtonClick={onBackButtonClick} />

            <div data-locator="planSuggestionsModalA">
                {isExploreYogaTypesDesktop ? (
                    <S.ContainerDesktop>
                        <Text medium center mb={24} type="h4">
                            {t('subscription.additionalContentOfferModal.modal.titleDesktop')}
                        </Text>
                        <InstallAppSteps
                            downloadButtonText="cancellationReasons.canNotLogin.modal.goToApp"
                            customLink={deepLinkPP}
                            cancelOfferName={offer}
                        />
                    </S.ContainerDesktop>
                ) : (
                    <S.Container>
                        <Text mb={12} type="h5">
                            {t('subscription.additionalContentOfferModal.modalA.title')}
                        </Text>
                        <Text mb={24} color="text-secondary">
                            {t('subscription.additionalContentOfferModal.modalA.disclaimer')}
                        </Text>

                        <AutoSlider sliderWidth={312} paddingBtwSlides={12}>
                            {sliderConfig.map(({ img, title, description, isCurrentPlan }) => (
                                <PersonalPlanCard
                                    img={img}
                                    title={title}
                                    description={description}
                                    key={title}
                                    isCurrentPlan={isCurrentPlan}
                                />
                            ))}
                        </AutoSlider>

                        <CustomPrimaryButton dataLocator="deeplinkBtn" onClick={handleDownloadApp} mb={16}>
                            {t('subscription.additionalContentOfferModal.modalA.exploreYogaTypes')}
                        </CustomPrimaryButton>

                        <OutlinedButton
                            onClick={() => {
                                sendAnalyticCancelOfferClick({
                                    eventLabel: { subscription_id, offer },
                                    actionEvent: CancelOfferAction.Cancel,
                                });
                                onCancelSubscription();
                            }}
                            text={t('basics.button.cancelSubscription')}
                            backgroundColor="surface-main"
                            dataLocator="helpLoginCancelSubscriptionButton"
                        />
                    </S.Container>
                )}
            </div>
        </Modal>
    );
};

export default AdditionalContentOfferModal;
